import { MouseEvent, useMemo } from 'react';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Button } from 'components/Button/Button';
import { DropdownChevron } from 'components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { closeDropdownFromElementWithin } from 'components/Dropdown/closeDropdownFromElementWithin';
import { DropdownListMenu } from 'components/Dropdown/menus/ListMenu/DropdownListMenu';
import { Hide } from 'components/Hide/Hide';
import { NotificationList } from 'components/NotificationList/NotificationList';
import { NotificationsDropdownIcon } from 'components/NotificationsDropdown/Icon/NotificationsDropdownIcon';
import { MappedLocalNotification } from 'types/LocalNotification/MappedLocalNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';

import {
  MobileNotificationsDropdownHeader,
  MobileNotificationsDropdownHeaderTitle,
  MobileNotificationsDropdownHolder,
} from './NotificationsDropdown.styled';

type Props = {
  notifications: (ApiNotification | MappedNotification)[];
  localNotifications: MappedLocalNotification[];
  onToggle: (open: boolean) => void;
  onClickTrigger?: (event: MouseEvent<HTMLElement>) => void;
};

export function NotificationsDropdown({
  notifications,
  localNotifications,
  onToggle,
  onClickTrigger,
}: Props) {
  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  const unseenLocalNotifications = useMemo(
    () => localNotifications.filter((notification) => !notification.viewed),
    [localNotifications],
  );

  const unseenNotificationsCount =
    unseenNotifications.length + unseenLocalNotifications.length;

  return (
    <Dropdown
      trigger={
        <DropdownHeaderTrigger
          variant="grey"
          qaId="notifications-dropdown-toggle"
          aria-label={getText('Notifications')}
          onClick={onClickTrigger}
        >
          <NotificationsDropdownIcon
            unseenNotificationsCount={unseenNotificationsCount}
          />

          <Hide xs sm md>
            <DropdownChevron />
          </Hide>
        </DropdownHeaderTrigger>
      }
      onToggle={onToggle}
    >
      <MobileNotificationsDropdownHolder data-qa-id="mobile-notifications-menu">
        <MobileNotificationsDropdownHeader>
          <MobileNotificationsDropdownHeaderTitle>
            <NotificationsDropdownIcon
              unseenNotificationsCount={unseenNotificationsCount}
            />
            <span>{getText('Notifications')}</span>
          </MobileNotificationsDropdownHeaderTitle>

          <Button
            type="button"
            data-qa-id="close-mobile-notifications"
            onClick={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            aria-label={getText('Close notifications')}
            variant="tertiary"
            noPadding
            icon={{ type: 'normalized', name: 'close' }}
          />
        </MobileNotificationsDropdownHeader>

        <NotificationList
          notifications={notifications}
          localNotifications={localNotifications}
          onClick={(event) =>
            closeDropdownFromElementWithin(event.target as HTMLElement)
          }
        />
      </MobileNotificationsDropdownHolder>

      <DropdownListMenu
        maxHeight={370}
        width={380}
        qaId="notifications-dropdown"
      >
        <NotificationList
          notifications={notifications}
          localNotifications={localNotifications}
          dismissible
          onClick={(event) =>
            closeDropdownFromElementWithin(event.target as HTMLElement)
          }
        />
      </DropdownListMenu>
    </Dropdown>
  );
}
